import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, Clock, Globe, MessageSquare, RefreshCw, XCircle, CheckCircle, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const API = '/api';

// Common timezones
const TIMEZONES = [
  { value: 'Europe/Berlin', label: 'Germany (CET/CEST)' },
  { value: 'Europe/London', label: 'UK (GMT/BST)' },
  { value: 'Europe/Paris', label: 'France (CET/CEST)' },
  { value: 'Asia/Dubai', label: 'UAE (GST)' },
  { value: 'Asia/Riyadh', label: 'Saudi Arabia (AST)' },
  { value: 'Asia/Kuwait', label: 'Kuwait (AST)' },
  { value: 'Asia/Qatar', label: 'Qatar (AST)' },
  { value: 'Africa/Cairo', label: 'Egypt (EET)' },
  { value: 'Africa/Johannesburg', label: 'South Africa (SAST)' },
  { value: 'Africa/Lagos', label: 'Nigeria (WAT)' },
  { value: 'America/New_York', label: 'US Eastern (EST/EDT)' },
  { value: 'America/Chicago', label: 'US Central (CST/CDT)' },
  { value: 'America/Los_Angeles', label: 'US Pacific (PST/PDT)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Asia/Tokyo', label: 'Japan (JST)' },
  { value: 'Australia/Sydney', label: 'Australia Eastern (AEST)' },
];

export default function InterviewReschedulePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [interview, setInterview] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Form state
  const [preferredDate, setPreferredDate] = useState('');
  const [preferredTime, setPreferredTime] = useState('');
  const [timezone, setTimezone] = useState('Europe/Berlin');
  const [note, setNote] = useState('');

  useEffect(() => {
    fetchInterviewDetails();
  }, [token]);

  const fetchInterviewDetails = async () => {
    try {
      const response = await fetch(`${API}/interview-response/${token}`);
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = 'Failed to load interview details';
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.detail || errorMessage;
        } catch {
          errorMessage = `Request failed: ${response.status}`;
        }
        throw new Error(errorMessage);
      }
      const data = await response.json();
      setInterview(data);
      
      // If already responded with reschedule, show message
      if (data.interview_schedule?.status === 'reschedule_requested') {
        setSuccess(true);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!preferredDate) {
      toast.error('Please select a preferred date');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API}/interview-response/${token}/reschedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          preferred_date: preferredDate,
          preferred_time: preferredTime || null,
          timezone: timezone,
          note: note || null,
        }),
      });

      if (!response.ok) {
        // Clone response before reading to avoid "body already read" error
        const errorText = await response.text();
        let errorMessage = 'Failed to submit reschedule request';
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.detail || errorMessage;
        } catch {
          // Response wasn't JSON, use status text
          errorMessage = `Request failed: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      // Success - we don't need to parse the response body
      setSuccess(true);
      toast.success('Reschedule request submitted successfully!');
    } catch (err) {
      toast.error(err.message || 'An unexpected error occurred');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#f8fafc] to-[#e0f2fe] flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <RefreshCw className="h-8 w-8 mx-auto mb-4 animate-spin text-[#33AADD]" />
            <p className="text-gray-600">Loading...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#f8fafc] to-[#e0f2fe] flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <XCircle className="h-12 w-12 mx-auto mb-4 text-red-500" />
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Error</h2>
            <p className="text-gray-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#fef3c7] to-[#fde68a] flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-amber-200">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-amber-100 rounded-full flex items-center justify-center">
              <CheckCircle className="h-10 w-10 text-amber-600" />
            </div>
            <h2 className="text-2xl font-bold text-amber-800 mb-2">Request Submitted!</h2>
            <p className="text-amber-700 mb-4">
              Your reschedule request for the <strong>{interview?.job_title}</strong> interview has been submitted.
            </p>
            <p className="text-sm text-amber-600">
              The recruiter will review your preferred time and get back to you shortly.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f8fafc] to-[#e0f2fe] py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-[#33AADD] mb-1">WeDo HR Services</h1>
          <p className="text-gray-600">Interview Scheduling</p>
        </div>

        {/* Back Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/interview-response/${token}`)}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to time options
        </Button>

        {/* Main Card */}
        <Card className="shadow-lg" data-testid="reschedule-form-card">
          <CardHeader className="bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-t-lg">
            <CardTitle className="text-xl">Request Different Time</CardTitle>
            <CardDescription className="text-white/90">
              {interview?.job_title}
            </CardDescription>
          </CardHeader>

          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Candidate Info */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">
                  Requesting reschedule for: <strong>{interview?.candidate_name}</strong>
                </p>
              </div>

              {/* Preferred Date */}
              <div>
                <Label htmlFor="preferredDate" className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-[#33AADD]" />
                  Preferred Date <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="preferredDate"
                  type="date"
                  value={preferredDate}
                  onChange={(e) => setPreferredDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="mt-1.5"
                  required
                  data-testid="preferred-date-input"
                />
              </div>

              {/* Preferred Time */}
              <div>
                <Label htmlFor="preferredTime" className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-[#33AADD]" />
                  Preferred Time (Optional)
                </Label>
                <Input
                  id="preferredTime"
                  type="time"
                  value={preferredTime}
                  onChange={(e) => setPreferredTime(e.target.value)}
                  className="mt-1.5"
                  data-testid="preferred-time-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Leave empty if you're flexible with the time
                </p>
              </div>

              {/* Timezone */}
              <div>
                <Label htmlFor="timezone" className="flex items-center gap-2">
                  <Globe className="h-4 w-4 text-[#33AADD]" />
                  Your Timezone
                </Label>
                <Select value={timezone} onValueChange={setTimezone}>
                  <SelectTrigger className="mt-1.5" data-testid="timezone-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIMEZONES.map(tz => (
                      <SelectItem key={tz.value} value={tz.value}>
                        {tz.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Note */}
              <div>
                <Label htmlFor="note" className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4 text-[#33AADD]" />
                  Additional Notes (Optional)
                </Label>
                <Textarea
                  id="note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Let us know if you have any other availability preferences or constraints..."
                  rows={3}
                  className="mt-1.5"
                  data-testid="reschedule-note"
                />
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full bg-amber-600 hover:bg-amber-700"
                disabled={submitting}
                data-testid="submit-reschedule-btn"
              >
                {submitting ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Reschedule Request'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-xs text-gray-500 mt-6">
          © {new Date().getFullYear()} WeDo HR Services UG • Germany
        </p>
      </div>
    </div>
  );
}
